import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HiOutlineCheck } from 'react-icons/hi2';
import cloudApi from '../../../services/cloudApi';
import McpTokenSetup from '../../../components/McpTokenSetup';

const ACCESS_LEVELS = [
  {
    value: 'read_only',
    title: 'Read-only',
    description: 'Analyze tables and check run status',
  },
  {
    value: 'full',
    title: 'Full access',
    description: 'Also run automations, clean up tables, and start new extractions (consumes credits)',
  },
];

// oauthAvailable: the backend's OAuth AS is live, so Claude connects by sign-in and
// the tokenized URL is demoted to a fallback for clients that can't set headers.
const CreateTokenModal = ({ isOpen, onClose, onCreated, onPlanRequired, oauthAvailable = false }) => {
  const [name, setName] = useState('Claude');
  const [accessLevel, setAccessLevel] = useState('read_only');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [created, setCreated] = useState(null);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  // Fresh form every time the modal opens
  useEffect(() => {
    if (isOpen) {
      setName('Claude');
      setAccessLevel('read_only');
      setCreating(false);
      setError(null);
      setCreated(null);
      setCopiedSnippet(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!name.trim() || creating) return;
    setCreating(true);
    setError(null);
    try {
      const result = await cloudApi.createMcpToken({ name: name.trim(), accessLevel });
      setCreated(result);
      // Notify the parent right away so the token survives even if the
      // modal is dismissed via the backdrop instead of the Done button.
      onCreated(result);
    } catch (err) {
      console.error('Failed to create MCP token:', err);
      if (err.code === 'PLAN_REQUIRED' || err.statusCode === 402) {
        // The parent closes this modal and shows the paid-plan upsell instead.
        if (onPlanRequired) onPlanRequired();
      } else {
        setError(err.message || 'Failed to create token.');
      }
    } finally {
      setCreating(false);
    }
  };

  const mcpUrl = cloudApi.getMcpServerUrl();

  // Rendered into <body>: embedders like the Integrations MCP card sit inside
  // `backdrop-blur` containers, and a filtered ancestor becomes the containing
  // block for `position: fixed` — which would trap the overlay inside the card
  // instead of covering the viewport (same fix as ConnectClaudeModal).
  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 animate-in fade-in duration-200 p-4" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden border border-gray-200/50 dark:border-gray-700/50 animate-in slide-in-from-bottom-3 duration-200 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-200/80 dark:border-gray-800 flex items-center justify-between">
          <div className="min-w-0 flex items-center gap-3">
            {created && (
              <span className="flex-shrink-0 w-8 h-8 rounded-full bg-emerald-50 dark:bg-emerald-500/10 ring-1 ring-emerald-500/20 dark:ring-emerald-400/20 flex items-center justify-center">
                <HiOutlineCheck className="w-4 h-4 text-emerald-500 dark:text-emerald-400" strokeWidth={2.5} />
              </span>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                {created ? 'Token created' : 'Create API token'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5">
                {created
                  ? 'One last step — connect your app below'
                  : oauthAvailable
                    ? 'For Claude Code, Cursor and scripts — Claude itself connects by sign-in, no token needed'
                    : 'For Claude and other MCP clients'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 p-2 rounded-xl text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 hover:scale-105 active:scale-95"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {created ? (
            <McpTokenSetup
              token={created.token}
              mcpUrl={mcpUrl}
              oauthAvailable={oauthAvailable}
              onCopied={() => setCopiedSnippet(true)}
            />
          ) : (
            <div className="space-y-5">
              {/* Name */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Token name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  maxLength={100}
                  placeholder="e.g. Claude"
                  onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                  className="w-full bg-white dark:bg-gray-700/60 text-gray-900 dark:text-white px-3 py-2 rounded-lg text-sm
                             border border-gray-300 dark:border-gray-600/50
                             hover:border-gray-400 dark:hover:border-gray-500/50
                             focus:border-blue-500 dark:focus:border-blue-400 focus:ring-1 focus:ring-blue-500/20 focus:outline-none
                             placeholder-gray-400 dark:placeholder-gray-500
                             transition-colors"
                />
              </div>

              {/* Access level */}
              <div>
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 block">Access level</label>
                <div className="space-y-2">
                  {ACCESS_LEVELS.map((level) => {
                    const isSelected = accessLevel === level.value;
                    return (
                      <button
                        key={level.value}
                        onClick={() => setAccessLevel(level.value)}
                        className={`relative w-full text-left p-3 pl-10 rounded-xl transition-all duration-200 ${
                          isSelected
                            ? 'bg-cyan-50 dark:bg-cyan-500/10 border border-cyan-400/70 dark:border-cyan-400/50 shadow-sm shadow-cyan-500/10'
                            : 'bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/40 hover:border-gray-300 dark:hover:border-gray-600/60 hover:bg-gray-100 dark:hover:bg-gray-800/60'
                        }`}
                      >
                        <span className={`absolute left-3.5 top-4 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                          isSelected
                            ? 'border-cyan-500 dark:border-cyan-400'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {isSelected && <span className="w-2 h-2 rounded-full bg-cyan-500 dark:bg-cyan-400" />}
                        </span>
                        <span className="block text-sm font-medium text-gray-900 dark:text-white">{level.title}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{level.description}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {error && (
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200/80 dark:border-gray-800 flex items-center justify-end gap-2">
          {created ? (
            <button
              onClick={onClose}
              className={`px-5 py-2.5 font-medium text-sm rounded-xl border transition-all duration-200 ${
                copiedSnippet
                  ? 'bg-emerald-600 hover:bg-emerald-500 text-white border-emerald-500'
                  : 'bg-gray-800 dark:bg-white/[0.1] text-white dark:text-gray-100 border-gray-700 dark:border-white/[0.08] hover:bg-gray-700 dark:hover:bg-white/[0.15]'
              }`}
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2.5 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/50 rounded-xl transition-all duration-200"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !name.trim()}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-white font-semibold text-sm rounded-xl bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-400 hover:to-cyan-400 shadow-sm shadow-cyan-500/25 transition-all duration-200 disabled:opacity-50 disabled:shadow-none disabled:cursor-not-allowed"
              >
                {creating ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                    Creating...
                  </span>
                ) : (
                  'Create token'
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default CreateTokenModal;
